/**
 * Country Risk Data for Risk Cartography
 * Contains GDPR status, known hacker groups, and cyber strategies by country
 * Sources: CNIL, ANSSI, ENISA, MITRE ATT&CK
 */

export const GDPR_STATUS = {
  // European Union - Full GDPR
  EU: {
    status: 'adequate',
    level: 1,
    description: 'Protection RGPD complete',
    color: '#10B981' // green
  },
  // Adequacy decisions by EU Commission
  ADEQUATE: {
    status: 'adequate',
    level: 1,
    description: 'Decision d\'adequation UE',
    color: '#10B981'
  },
  // Partial adequacy or ongoing negotiations
  PARTIAL: {
    status: 'partial',
    level: 2,
    description: 'Protection partielle',
    color: '#F59E0B' // yellow
  },
  // No adequacy decision
  NONE: {
    status: 'none',
    level: 3,
    description: 'Pas de decision d\'adequation',
    color: '#EF4444' // red
  },
  // Unknown or not assessed
  UNKNOWN: {
    status: 'unknown',
    level: 4,
    description: 'Non evalue',
    color: '#6B7280' // gray
  }
};

// Country data with GDPR status, hacker groups, and cyber strategies
export const COUNTRY_RISK_DATA = {
  // === EUROPEAN UNION ===
  FR: {
    name: 'France',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'low',
    hackerGroups: ['APT28 (targets)', 'Turla (targets)'],
    cyberStrategy: 'Strategie nationale de cybersecurite 2021-2025, ANSSI',
    notes: 'Centre de excellence cyber europeen, PASSI certification',
    dataProtectionAuthority: 'CNIL',
    coordinates: [46.2276, 2.2137]
  },
  DE: {
    name: 'Allemagne',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'low',
    hackerGroups: ['APT28 (targets)', 'Sandworm (targets)'],
    cyberStrategy: 'Cyber Security Strategy 2021, BSI',
    notes: 'Leader europeen en protection des donnees',
    dataProtectionAuthority: 'BfDI',
    coordinates: [51.1657, 10.4515]
  },
  IT: {
    name: 'Italie',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'National Cybersecurity Strategy 2022-2026',
    notes: 'ACN - Agenzia per la Cybersicurezza Nazionale',
    dataProtectionAuthority: 'Garante Privacy',
    coordinates: [41.8719, 12.5674]
  },
  ES: {
    name: 'Espagne',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'Estrategia Nacional de Ciberseguridad 2019',
    notes: 'INCIBE, CCN-CERT',
    dataProtectionAuthority: 'AEPD',
    coordinates: [40.4637, -3.7492]
  },
  NL: {
    name: 'Pays-Bas',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'Nederlandse Cybersecurity Strategie 2022-2028',
    notes: 'NCSC-NL, European Cybercrime Centre',
    dataProtectionAuthority: 'Autoriteit Persoonsgegevens',
    coordinates: [52.1326, 5.2913]
  },
  BE: {
    name: 'Belgique',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'Strategie Cybersecurite Belgique 2.0',
    notes: 'Centre for Cyber Security Belgium (CCB)',
    dataProtectionAuthority: 'APD',
    coordinates: [50.8503, 4.3517]
  },
  PT: {
    name: 'Portugal',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'Estrategia Nacional de Seguranca do Ciberespaco',
    notes: 'Centro Nacional de Ciberseguranca (CNCS)',
    dataProtectionAuthority: 'CNPD',
    coordinates: [39.3999, -8.2245]
  },
  AT: {
    name: 'Autriche',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'Austrian Cyber Security Strategy',
    notes: 'Cyber Security Center Austria',
    dataProtectionAuthority: 'DSB',
    coordinates: [47.5162, 14.5501]
  },
  PL: {
    name: 'Pologne',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'medium',
    hackerGroups: ['GhostWriter (targets)'],
    cyberStrategy: 'Cybersecurity Strategy of the Republic of Poland',
    notes: 'NASK, proximite conflit ukrainien',
    dataProtectionAuthority: 'UODO',
    coordinates: [51.9194, 19.1451]
  },
  SE: {
    name: 'Suede',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'National Cyber Security Strategy',
    notes: 'MSB, CERT-SE',
    dataProtectionAuthority: 'IMY',
    coordinates: [60.1282, 18.6435]
  },
  FI: {
    name: 'Finlande',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'low',
    hackerGroups: ['APT29 (targets)'],
    cyberStrategy: 'Finland\'s Cyber Security Strategy 2019',
    notes: 'Traficom, frontiere russe',
    dataProtectionAuthority: 'Tietosuojavaltuutettu',
    coordinates: [61.9241, 25.7482]
  },
  DK: {
    name: 'Danemark',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'Danish Cyber and Information Security Strategy 2022-2024',
    notes: 'CFCS',
    dataProtectionAuthority: 'Datatilsynet',
    coordinates: [56.2639, 9.5018]
  },
  IE: {
    name: 'Irlande',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'National Cyber Security Strategy 2019-2024',
    notes: 'NCSC Ireland, siege GAFAM europeen',
    dataProtectionAuthority: 'DPC',
    coordinates: [53.1424, -7.6921]
  },
  GR: {
    name: 'Grece',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'National Cyber Security Strategy',
    notes: 'National Cyber Security Authority',
    dataProtectionAuthority: 'HDPA',
    coordinates: [39.0742, 21.8243]
  },
  CZ: {
    name: 'Republique Tcheque',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'medium',
    hackerGroups: ['Gamaredon (targets)'],
    cyberStrategy: 'National Cyber Security Strategy 2021-2025',
    notes: 'NUKIB',
    dataProtectionAuthority: 'UOOU',
    coordinates: [49.8175, 15.4730]
  },
  RO: {
    name: 'Roumanie',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'medium',
    hackerGroups: [],
    cyberStrategy: 'Romania Cyber Security Strategy',
    notes: 'CERT-RO',
    dataProtectionAuthority: 'ANSPDCP',
    coordinates: [45.9432, 24.9668]
  },
  HU: {
    name: 'Hongrie',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'Hungary\'s National Cyber Security Strategy',
    notes: 'NBSZ',
    dataProtectionAuthority: 'NAIH',
    coordinates: [47.1625, 19.5033]
  },
  SK: {
    name: 'Slovaquie',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'National Cyber Security Strategy',
    notes: 'NBU',
    dataProtectionAuthority: 'UOOU',
    coordinates: [48.6690, 19.6990]
  },
  BG: {
    name: 'Bulgarie',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'medium',
    hackerGroups: [],
    cyberStrategy: 'National Cyber Security Strategy',
    notes: 'CERT Bulgaria',
    dataProtectionAuthority: 'CPDP',
    coordinates: [42.7339, 25.4858]
  },
  HR: {
    name: 'Croatie',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'National Cyber Security Strategy',
    notes: 'CERT.hr',
    dataProtectionAuthority: 'AZOP',
    coordinates: [45.1, 15.2]
  },
  SI: {
    name: 'Slovenie',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'Cyber Security Strategy',
    notes: 'SI-CERT',
    dataProtectionAuthority: 'IP-RS',
    coordinates: [46.1512, 14.9955]
  },
  LT: {
    name: 'Lituanie',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'medium',
    hackerGroups: ['GhostWriter (targets)'],
    cyberStrategy: 'National Cyber Security Strategy 2018-2023',
    notes: 'NRD CERT, frontiere Russie/Bielorussie',
    dataProtectionAuthority: 'VDAI',
    coordinates: [55.1694, 23.8813]
  },
  LV: {
    name: 'Lettonie',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'medium',
    hackerGroups: ['GhostWriter (targets)'],
    cyberStrategy: 'Cyber Security Strategy of Latvia',
    notes: 'CERT.LV',
    dataProtectionAuthority: 'DVI',
    coordinates: [56.8796, 24.6032]
  },
  EE: {
    name: 'Estonie',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'medium',
    hackerGroups: ['APT28 (targets)', 'Sandworm (targets)'],
    cyberStrategy: 'Cyber Security Strategy 2019-2022',
    notes: 'CERT-EE, NATO CCDCOE, leader e-governance',
    dataProtectionAuthority: 'AKI',
    coordinates: [58.5953, 25.0136]
  },
  CY: {
    name: 'Chypre',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'National Cyber Security Strategy',
    notes: 'CSIRT-CY',
    dataProtectionAuthority: 'Office of the Commissioner',
    coordinates: [35.1264, 33.4299]
  },
  MT: {
    name: 'Malte',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'Malta Cyber Security Strategy',
    notes: 'CSIRTMalta',
    dataProtectionAuthority: 'IDPC',
    coordinates: [35.9375, 14.3754]
  },
  LU: {
    name: 'Luxembourg',
    gdpr: GDPR_STATUS.EU,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'National Cybersecurity Strategy III',
    notes: 'CIRCL, GOVCERT.LU',
    dataProtectionAuthority: 'CNPD',
    coordinates: [49.8153, 6.1296]
  },

  // === ADEQUACY DECISIONS (Non-EU) ===
  GB: {
    name: 'Royaume-Uni',
    gdpr: GDPR_STATUS.ADEQUATE,
    threatLevel: 'medium',
    hackerGroups: ['APT29 (targets)', 'Turla (targets)'],
    cyberStrategy: 'National Cyber Strategy 2022',
    notes: 'NCSC UK, GCHQ, post-Brexit adequacy 2021',
    dataProtectionAuthority: 'ICO',
    coordinates: [55.3781, -3.4360]
  },
  CH: {
    name: 'Suisse',
    gdpr: GDPR_STATUS.ADEQUATE,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'National Cybersecurity Strategy 2023-2027',
    notes: 'NCSC Suisse, loi LPD revisee 2023',
    dataProtectionAuthority: 'PFPDT',
    coordinates: [46.8182, 8.2275]
  },
  NO: {
    name: 'Norvege',
    gdpr: GDPR_STATUS.ADEQUATE,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'National Cyber Security Strategy',
    notes: 'NSM, membre EEE',
    dataProtectionAuthority: 'Datatilsynet',
    coordinates: [60.4720, 8.4689]
  },
  IS: {
    name: 'Islande',
    gdpr: GDPR_STATUS.ADEQUATE,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'National Cyber Security Strategy',
    notes: 'CERT-IS, membre EEE',
    dataProtectionAuthority: 'Personuvernd',
    coordinates: [64.9631, -19.0208]
  },
  JP: {
    name: 'Japon',
    gdpr: GDPR_STATUS.ADEQUATE,
    threatLevel: 'medium',
    hackerGroups: ['APT10 (targets)', 'Lazarus (targets)'],
    cyberStrategy: 'Cybersecurity Strategy',
    notes: 'NISC, adequation UE 2019',
    dataProtectionAuthority: 'PPC',
    coordinates: [36.2048, 138.2529]
  },
  KR: {
    name: 'Coree du Sud',
    gdpr: GDPR_STATUS.ADEQUATE,
    threatLevel: 'high',
    hackerGroups: ['Lazarus (targets)', 'Kimsuky (targets)'],
    cyberStrategy: 'National Cybersecurity Strategy',
    notes: 'KISA, adequation UE 2022, menace Nord-Coreenne',
    dataProtectionAuthority: 'PIPC',
    coordinates: [35.9078, 127.7669]
  },
  NZ: {
    name: 'Nouvelle-Zelande',
    gdpr: GDPR_STATUS.ADEQUATE,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'Cyber Security Strategy 2019',
    notes: 'CERT NZ, adequation UE',
    dataProtectionAuthority: 'OPC',
    coordinates: [-40.9006, 174.8860]
  },
  CA: {
    name: 'Canada',
    gdpr: GDPR_STATUS.ADEQUATE,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'National Cyber Security Strategy',
    notes: 'CCCS, adequation UE pour transferts commerciaux',
    dataProtectionAuthority: 'OPC',
    coordinates: [56.1304, -106.3468]
  },
  AR: {
    name: 'Argentine',
    gdpr: GDPR_STATUS.ADEQUATE,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'Estrategia Nacional de Ciberseguridad',
    notes: 'CERT.ar, adequation UE 2003',
    dataProtectionAuthority: 'AAIP',
    coordinates: [-38.4161, -63.6167]
  },
  UY: {
    name: 'Uruguay',
    gdpr: GDPR_STATUS.ADEQUATE,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'Agenda Digital Uruguay',
    notes: 'CERTuy, adequation UE 2012',
    dataProtectionAuthority: 'URCDP',
    coordinates: [-32.5228, -55.7658]
  },
  IL: {
    name: 'Israel',
    gdpr: GDPR_STATUS.ADEQUATE,
    threatLevel: 'high',
    hackerGroups: ['Unit 8200 (state)', 'Various nation-state targets'],
    cyberStrategy: 'National Cyber Security Strategy',
    notes: 'INCD, adequation UE 2011, leader cyber mondial',
    dataProtectionAuthority: 'PPA',
    coordinates: [31.0461, 34.8516]
  },

  // === PARTIAL PROTECTION ===
  US: {
    name: 'Etats-Unis',
    gdpr: GDPR_STATUS.PARTIAL,
    threatLevel: 'medium',
    hackerGroups: ['APT29 (targets)', 'Various nation-state targets'],
    cyberStrategy: 'National Cybersecurity Strategy 2023',
    notes: 'CISA, DPF 2023 (remplace Privacy Shield), pas adequation complete',
    dataProtectionAuthority: 'FTC (federal), State AGs',
    coordinates: [37.0902, -95.7129]
  },
  AU: {
    name: 'Australie',
    gdpr: GDPR_STATUS.PARTIAL,
    threatLevel: 'medium',
    hackerGroups: ['APT40 (targets)'],
    cyberStrategy: 'Australia\'s Cyber Security Strategy 2023-2030',
    notes: 'ACSC, Privacy Act en cours de revision',
    dataProtectionAuthority: 'OAIC',
    coordinates: [-25.2744, 133.7751]
  },
  BR: {
    name: 'Bresil',
    gdpr: GDPR_STATUS.PARTIAL,
    threatLevel: 'medium',
    hackerGroups: ['Poseidon Group', 'Blind Eagle (targets)'],
    cyberStrategy: 'E-Ciber',
    notes: 'LGPD 2020, en cours d\'adequation UE',
    dataProtectionAuthority: 'ANPD',
    coordinates: [-14.2350, -51.9253]
  },
  IN: {
    name: 'Inde',
    gdpr: GDPR_STATUS.PARTIAL,
    threatLevel: 'medium',
    hackerGroups: ['SideWinder', 'Patchwork', 'Dropping Elephant'],
    cyberStrategy: 'National Cyber Security Policy',
    notes: 'CERT-In, Digital Personal Data Protection Act 2023',
    dataProtectionAuthority: 'En cours de creation',
    coordinates: [20.5937, 78.9629]
  },
  SG: {
    name: 'Singapour',
    gdpr: GDPR_STATUS.PARTIAL,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'Singapore Cybersecurity Strategy 2021',
    notes: 'CSA, PDPA, hub technologique Asie',
    dataProtectionAuthority: 'PDPC',
    coordinates: [1.3521, 103.8198]
  },
  AE: {
    name: 'Emirats Arabes Unis',
    gdpr: GDPR_STATUS.PARTIAL,
    threatLevel: 'medium',
    hackerGroups: ['DarkMatter (alleged state)'],
    cyberStrategy: 'UAE Cybersecurity Strategy',
    notes: 'NESA, DIFC PDPL, ADGM DP',
    dataProtectionAuthority: 'Multiple (DIFC, ADGM)',
    coordinates: [23.4241, 53.8478]
  },
  MX: {
    name: 'Mexique',
    gdpr: GDPR_STATUS.PARTIAL,
    threatLevel: 'medium',
    hackerGroups: [],
    cyberStrategy: 'Estrategia Nacional de Ciberseguridad',
    notes: 'CERT-MX, LFPDPPP',
    dataProtectionAuthority: 'INAI',
    coordinates: [23.6345, -102.5528]
  },
  ZA: {
    name: 'Afrique du Sud',
    gdpr: GDPR_STATUS.PARTIAL,
    threatLevel: 'medium',
    hackerGroups: [],
    cyberStrategy: 'National Cybersecurity Policy Framework',
    notes: 'POPIA 2020',
    dataProtectionAuthority: 'Information Regulator',
    coordinates: [-30.5595, 22.9375]
  },
  TH: {
    name: 'Thailande',
    gdpr: GDPR_STATUS.PARTIAL,
    threatLevel: 'medium',
    hackerGroups: [],
    cyberStrategy: 'Thailand Cybersecurity Strategy',
    notes: 'ThaiCERT, PDPA 2019',
    dataProtectionAuthority: 'PDPC Thailand',
    coordinates: [15.8700, 100.9925]
  },
  MY: {
    name: 'Malaisie',
    gdpr: GDPR_STATUS.PARTIAL,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'Malaysia Cyber Security Strategy',
    notes: 'MyCERT, PDPA 2010',
    dataProtectionAuthority: 'JPDP',
    coordinates: [4.2105, 101.9758]
  },
  PH: {
    name: 'Philippines',
    gdpr: GDPR_STATUS.PARTIAL,
    threatLevel: 'medium',
    hackerGroups: [],
    cyberStrategy: 'National Cybersecurity Plan 2022',
    notes: 'DICT, DPA 2012',
    dataProtectionAuthority: 'NPC',
    coordinates: [12.8797, 121.7740]
  },
  TR: {
    name: 'Turquie',
    gdpr: GDPR_STATUS.PARTIAL,
    threatLevel: 'medium',
    hackerGroups: ['Sea Turtle'],
    cyberStrategy: 'National Cyber Security Strategy',
    notes: 'USOM, KVKK',
    dataProtectionAuthority: 'KVKK',
    coordinates: [38.9637, 35.2433]
  },
  CO: {
    name: 'Colombie',
    gdpr: GDPR_STATUS.PARTIAL,
    threatLevel: 'medium',
    hackerGroups: ['Blind Eagle (targets)'],
    cyberStrategy: 'CONPES 3995',
    notes: 'colCERT, Ley 1581 de 2012',
    dataProtectionAuthority: 'SIC',
    coordinates: [4.5709, -74.2973]
  },
  CL: {
    name: 'Chili',
    gdpr: GDPR_STATUS.PARTIAL,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'Politica Nacional de Ciberseguridad',
    notes: 'CSIRT Chile',
    dataProtectionAuthority: 'En cours de creation',
    coordinates: [-35.6751, -71.5430]
  },

  // === NO ADEQUACY / HIGH RISK ===
  RU: {
    name: 'Russie',
    gdpr: GDPR_STATUS.NONE,
    threatLevel: 'critical',
    hackerGroups: [
      'APT28 (Fancy Bear) - GRU',
      'APT29 (Cozy Bear) - FSB/SVR',
      'Sandworm - GRU',
      'Turla - FSB',
      'Gamaredon - FSB',
      'GhostWriter',
      'Conti',
      'REvil',
      'LockBit (affilies)'
    ],
    cyberStrategy: 'Doctrine de securite informationnelle 2016',
    notes: 'Menace majeure, operations cyber offensives actives, ransomware-as-a-service',
    dataProtectionAuthority: 'Roskomnadzor',
    coordinates: [61.5240, 105.3188]
  },
  CN: {
    name: 'Chine',
    gdpr: GDPR_STATUS.NONE,
    threatLevel: 'critical',
    hackerGroups: [
      'APT1 (Comment Crew) - PLA Unit 61398',
      'APT10 (Stone Panda) - MSS',
      'APT40 (Leviathan) - MSS',
      'APT41 (Double Dragon) - MSS/Criminal',
      'Hafnium - MSS',
      'Winnti Group',
      'Mustang Panda',
      'Volt Typhoon',
      'Salt Typhoon'
    ],
    cyberStrategy: 'Strategie de cybersecurite nationale, Loi sur la cybersecurite 2017',
    notes: 'Espionnage industriel massif, vol de propriete intellectuelle, PIPL 2021',
    dataProtectionAuthority: 'CAC',
    coordinates: [35.8617, 104.1954]
  },
  KP: {
    name: 'Coree du Nord',
    gdpr: GDPR_STATUS.NONE,
    threatLevel: 'critical',
    hackerGroups: [
      'Lazarus Group - RGB',
      'Kimsuky - RGB',
      'APT37 (Reaper) - RGB',
      'APT38 - RGB',
      'Andariel',
      'BlueNoroff'
    ],
    cyberStrategy: 'Operations cyber pour financement regime (crypto)',
    notes: 'Cyberattaques financieres, vol crypto, ransomware, sanctions ONU',
    dataProtectionAuthority: 'Aucune',
    coordinates: [40.3399, 127.5101]
  },
  IR: {
    name: 'Iran',
    gdpr: GDPR_STATUS.NONE,
    threatLevel: 'critical',
    hackerGroups: [
      'APT33 (Elfin) - MOIS',
      'APT34 (OilRig) - MOIS',
      'APT35 (Charming Kitten) - IRGC',
      'APT39 (Chafer) - MOIS',
      'MuddyWater - MOIS',
      'Phosphorus'
    ],
    cyberStrategy: 'Strategie cyber offensive',
    notes: 'Operations destructrices, ciblage secteur energie/petrole',
    dataProtectionAuthority: 'Aucune',
    coordinates: [32.4279, 53.6880]
  },
  BY: {
    name: 'Bielorussie',
    gdpr: GDPR_STATUS.NONE,
    threatLevel: 'high',
    hackerGroups: ['GhostWriter - government-linked', 'UNC1151'],
    cyberStrategy: 'Alignee sur Russie',
    notes: 'Operations info-guerre, desinformation',
    dataProtectionAuthority: 'Aucune',
    coordinates: [53.7098, 27.9534]
  },
  VN: {
    name: 'Vietnam',
    gdpr: GDPR_STATUS.NONE,
    threatLevel: 'high',
    hackerGroups: ['APT32 (OceanLotus) - MPS', 'APT-C-00'],
    cyberStrategy: 'Cybersecurity Law 2018',
    notes: 'Surveillance, ciblage dissidents',
    dataProtectionAuthority: 'MIC',
    coordinates: [14.0583, 108.2772]
  },
  PK: {
    name: 'Pakistan',
    gdpr: GDPR_STATUS.NONE,
    threatLevel: 'high',
    hackerGroups: ['APT36 (Transparent Tribe) - ISI', 'SideCopy'],
    cyberStrategy: 'National Cyber Security Policy',
    notes: 'Ciblage Inde, espionnage regional',
    dataProtectionAuthority: 'Aucune',
    coordinates: [30.3753, 69.3451]
  },
  NG: {
    name: 'Nigeria',
    gdpr: GDPR_STATUS.NONE,
    threatLevel: 'high',
    hackerGroups: ['SilverTerrier', 'Groupes BEC'],
    cyberStrategy: 'National Cybersecurity Strategy',
    notes: 'Fraude BEC, cybercriminalite financiere',
    dataProtectionAuthority: 'NDPR',
    coordinates: [9.0820, 8.6753]
  },
  UA: {
    name: 'Ukraine',
    gdpr: GDPR_STATUS.PARTIAL,
    threatLevel: 'high',
    hackerGroups: ['IT Army of Ukraine (defense)'],
    cyberStrategy: 'Cyber Security Strategy 2021',
    notes: 'Zone de conflit actif, cible d\'attaques russes constantes',
    dataProtectionAuthority: 'UAPC',
    coordinates: [48.3794, 31.1656]
  },
  VE: {
    name: 'Venezuela',
    gdpr: GDPR_STATUS.NONE,
    threatLevel: 'medium',
    hackerGroups: [],
    cyberStrategy: 'Ley Especial contra Delitos Informaticos',
    notes: 'Surveillance gouvernementale, instabilite',
    dataProtectionAuthority: 'Aucune',
    coordinates: [6.4238, -66.5897]
  },
  CU: {
    name: 'Cuba',
    gdpr: GDPR_STATUS.NONE,
    threatLevel: 'medium',
    hackerGroups: [],
    cyberStrategy: 'Controle etatique internet',
    notes: 'Acces internet restreint, surveillance',
    dataProtectionAuthority: 'Aucune',
    coordinates: [21.5218, -77.7812]
  },
  SA: {
    name: 'Arabie Saoudite',
    gdpr: GDPR_STATUS.PARTIAL,
    threatLevel: 'medium',
    hackerGroups: [],
    cyberStrategy: 'National Cybersecurity Strategy',
    notes: 'NCA, PDPL 2021',
    dataProtectionAuthority: 'SDAIA',
    coordinates: [23.8859, 45.0792]
  },
  EG: {
    name: 'Egypte',
    gdpr: GDPR_STATUS.NONE,
    threatLevel: 'medium',
    hackerGroups: [],
    cyberStrategy: 'National Cybersecurity Strategy',
    notes: 'EG-CERT, surveillance',
    dataProtectionAuthority: 'IDPR',
    coordinates: [26.8206, 30.8025]
  },
  ID: {
    name: 'Indonesie',
    gdpr: GDPR_STATUS.PARTIAL,
    threatLevel: 'medium',
    hackerGroups: [],
    cyberStrategy: 'National Cyber Security Strategy',
    notes: 'BSSN, UU PDP 2022',
    dataProtectionAuthority: 'KOMINFO',
    coordinates: [-0.7893, 113.9213]
  },
  BD: {
    name: 'Bangladesh',
    gdpr: GDPR_STATUS.NONE,
    threatLevel: 'medium',
    hackerGroups: [],
    cyberStrategy: 'Digital Bangladesh',
    notes: 'BGD e-GOV CIRT',
    dataProtectionAuthority: 'En cours',
    coordinates: [23.6850, 90.3563]
  },
  KE: {
    name: 'Kenya',
    gdpr: GDPR_STATUS.PARTIAL,
    threatLevel: 'medium',
    hackerGroups: [],
    cyberStrategy: 'National Cybersecurity Strategy',
    notes: 'KE-CIRT/CC, Data Protection Act 2019',
    dataProtectionAuthority: 'ODPC',
    coordinates: [-0.0236, 37.9062]
  },
  MA: {
    name: 'Maroc',
    gdpr: GDPR_STATUS.PARTIAL,
    threatLevel: 'medium',
    hackerGroups: [],
    cyberStrategy: 'Strategie Nationale de Cybersecurite',
    notes: 'maCERT, CNDP',
    dataProtectionAuthority: 'CNDP',
    coordinates: [31.7917, -7.0926]
  },
  DZ: {
    name: 'Algerie',
    gdpr: GDPR_STATUS.NONE,
    threatLevel: 'medium',
    hackerGroups: [],
    cyberStrategy: 'Strategie Nationale Cybersecurite',
    notes: 'CERT.dz',
    dataProtectionAuthority: 'ANPDP',
    coordinates: [28.0339, 1.6596]
  },
  TN: {
    name: 'Tunisie',
    gdpr: GDPR_STATUS.PARTIAL,
    threatLevel: 'low',
    hackerGroups: [],
    cyberStrategy: 'Strategie Nationale Cybersecurite',
    notes: 'TunCERT, INPDP',
    dataProtectionAuthority: 'INPDP',
    coordinates: [33.8869, 9.5375]
  }
};

// Get threat level color
export const getThreatLevelColor = (level) => {
  switch (level) {
    case 'critical': return '#DC2626'; // red-600
    case 'high': return '#F97316'; // orange-500
    case 'medium': return '#F59E0B'; // amber-500
    case 'low': return '#10B981'; // green-500
    default: return '#6B7280'; // gray-500
  }
};

// Get threat level label
export const getThreatLevelLabel = (level) => {
  switch (level) {
    case 'critical': return 'Critique';
    case 'high': return 'Eleve';
    case 'medium': return 'Moyen';
    case 'low': return 'Faible';
    default: return 'Inconnu';
  }
};

// Get country data by ISO code
export const getCountryData = (isoCode) => {
  return COUNTRY_RISK_DATA[isoCode?.toUpperCase()] || null;
};

// Get all countries with specific threat level
export const getCountriesByThreatLevel = (level) => {
  return Object.entries(COUNTRY_RISK_DATA)
    .filter(([_, data]) => data.threatLevel === level)
    .map(([code, data]) => ({ code, ...data }));
};

// Get all APT groups
export const getAllAPTGroups = () => {
  const groups = new Set();
  Object.values(COUNTRY_RISK_DATA).forEach(country => {
    country.hackerGroups.forEach(group => groups.add(group));
  });
  return Array.from(groups).sort();
};
